import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Layout from "src/components/app/layout/Layout";
import Header from "src/components/app/layout/Header";
import Input from "src/components/app/Input";
import Button from "src/components/app/Button";
import LabTestListScreen from "src/components/app/lab-tests/LabTestListScreen";
import AddLocationBottomSheet from "src/components/app/bottom-sheet/AddLocationBottomSheet";

import { Search, ShoppingCart } from "lucide-react";
import { useAppSelector } from "src/redux/hooks";
import { useLabTestCart } from "src/helpers/common";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";

const BookLabTestScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const [isLocationOpen, setIsLocationOpen] = useState<boolean>(false);
  const locationRef = useRef<HTMLDivElement>(null);
  const getLabTestCart = useAppSelector(
    (state) => state.labTest.getLabTestCart,
  );
  const cartCount: number = getLabTestCart?.data?.tests_list?.length || 0;
  const addedTestsInCart = getLabTestCart?.data?.tests_list || [];
  const testIdsPresentInCart = addedTestsInCart.map((test) => ({
    lab_test_name: test.name,
    code: test.code,
  }));
  const { fetchLabTestCart } = useLabTestCart();

  useEffect(() => {
    // Event: BOOK_LAB_TEST_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.BOOK_LAB_TEST_SCREEN);
    fetchLabTestCart();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        locationRef.current &&
        !locationRef.current.contains(event.target as Node)
      ) {
        setIsLocationOpen(false);
      }
    };

    if (isLocationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLocationOpen]);

  // const onPressLocation = () => {
  //   setIsLocationOpen(!isLocationOpen);
  // };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {};

  const navigateToCheckout = () => {
    if (cartCount === 0) {
      return;
    }
    navigate(`/app/${supportProgramKey}/book-lab-test/checkout`);
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div
        style={{ height: "calc(100vh - 75px)" }}
        className="relative transition-all duration-300 pt-32"
      >
        <div className="fixed top-0 left-0 right-0 z-10 max-w-lg mx-auto">
          <Header
            title="All Lab Tests"
            titlePosition="left"
            onBackClick={() =>
              navigate(`/app/${supportProgramKey}/home-screen`)
            }
            // rightComponent={
            //   <div
            //     className="relative w-6 h-6 mt-2"
            //     onClick={() => {
            //       navigateToCheckout();
            //     }}
            //   >
            //     <ShoppingCart className="w-6 h-6 text-gray-800" />
            //     {cartCount > 0 && (
            //       <span
            //         className="absolute -top-1 -right-1  text-white text-[10px] font-medium rounded-full w-4 h-4 flex items-center justify-center leading-none"
            //         style={{ backgroundColor: "var(--app-primary)" }}
            //       >
            //         {cartCount}
            //       </span>
            //     )}
            //   </div>
            // }
          />
          {/* Location pin code section - Commented out as per requirement */}
          {/* <div
            className="flex items-center px-4 py-2 text-gray-600 bg-gray-50"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === "Enter" && onPressLocation()}
          >
            <MapPin size={16} />
            <span className="location-text text-sm" onClick={onPressLocation}>
              Bengaluru 560068
            </span>
            <ChevronDown size={16} onClick={onPressLocation} />
          </div> */}

          <div className="p-4">
            <Input
              name="search"
              type="text"
              placeholder="Search for Tests, Health Packages"
              fullWidth
              onChange={handleSearchChange}
              startAdornment={<Search className="w-4 h-4" />}
              onClick={() => {
                navigate(
                  `/app/${supportProgramKey}/book-lab-test/search-lab-tests`,
                );
              }}
            />
          </div>
        </div>
        {/* Removed overflow-y-auto from here to fix duplicate scroll issue - scroll is handled inside LabTestListScreen */}
        <div className="h-[calc(100vh-57px-70px-80px)] space-y-3 mb-20">
          <LabTestListScreen
            testIdsPresentInCart={testIdsPresentInCart}
            cartCount={cartCount}
            onCartUpdate={fetchLabTestCart}
          />
        </div>

        {!isLocationOpen && (
          <div className="fixed rounded-t-lg max-w-lg mx-auto w-full min-h-20 bg-white bottom-0 left-0 right-0 z-10 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] bg-black/50">
            <div className="flex justify-between items-center p-4">
              <div>
                <p className="text-sm text-gray-700">
                  {cartCount || "No"} Test{cartCount > 1 ? "s" : ""} Added
                </p>
                <p className="text-lg font-semibold text-black">
                  ₹ {getLabTestCart?.data?.order_detail?.order_total || 0}
                </p>
              </div>
              <Button
                rounded="full"
                disabled={cartCount === 0}
                onClick={() => {
                  navigateToCheckout();
                }}
              >
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-white" />
                  <span className="text-white">View Cart</span>
                </div>
              </Button>
            </div>
          </div>
        )}

        <AddLocationBottomSheet
          showBottomSheet={isLocationOpen}
          setShowBottomSheet={setIsLocationOpen}
        />
      </div>
    </Layout>
  );
};

export default BookLabTestScreen;
